import { useEffect, useRef, useState } from "react";
import { icons, images } from "helpers/images";
import "../../../assets/css/landingStyle.css";
import { ArrowDownTrayIcon, ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Bars3BottomLeftIcon, PhoneIcon } from "@heroicons/react/24/solid";
import useOnClickOutside from "helpers/useOnClickOutside";
import DropdownClickOutside from "helpers/dropdownClickOutside";
import CommonAccordion from "../Accordion";
import { getDownloadAppLink, deviceInfo, getUtmLink } from "helpers";
import { Link, useHistory } from "react-router-dom/cjs/react-router-dom.min";

const LandingHeaderComponent = ({ handleScroll, landingHeaderRef }) => {
  const deviceType = deviceInfo();

  const menuRef = useRef();
  const examsRef = useRef();
  const examsLabelRef = useRef();
  const exploreRef = useRef();
  const exploreLabelRef = useRef();
  const aboutRef = useRef();
  const aboutLabelRef = useRef();
  const [showExams, setShowExams] = useState(false);
  const [showExplore, setShowExplore] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const history = useHistory();

  const examList = [
    {
      to: "/neet-ug",
      label: "NEET UG",
    },
    {
      to: "/neet-pg",
      label: "NEET PG",
    },
    {
      to: "/neet-mds",
      label: "NEET MDS",
    },
    {
      to: "/inicet",
      label: "INICET",
    },
    {
      to: "/dnb-pdcet",
      label: "DNB PDCET",
    },
    {
      to: "/neet-ss",
      label: "NEET SS",
    },
  ];

  const exploreList = [
    {
      to: "/institutes",
      label: "Institutes",
    },
    {
      to: "/videos",
      label: "Videos",
    },
  ];

  const aboutUsList = [
    {
      to: "/about-us",
      label: "About us",
    },
    {
      to: "/careers",
      label: "Careers",
    },
    {
      to: "/testimonials",
      label: "Testimonials",
    },
    {
      to: "/contact-us",
      label: "Contact us",
    },
  ];

  const blogsAndNewsOption = () => {
    return (
      <a
        href="/blogs"
        target="_self"
        rel="noopener noreferrer"
        className="flex items-center w-full h-full !text-base cursor-pointer"
      >
        Blogs & News
      </a>
    );
  };

  const loginOption = () => {
    return (
      <a
        href="/portal/dashboard"
        target="_self"
        className="flex-1 !px-10 !py-3 rounded-4xl text-center text-sm font-bold text-white bg-primary cursor-pointer"
      >
        Login
      </a>
    );
  };

  const PackagesOption = () => {
    return (
      <a
        href="/packages"
        target="_self"
        className="flex items-center w-full h-full !text-base cursor-pointer"
      >
        Packages
      </a>
    );
  };

  const ZytalesOption = () => {
    return (
      <a
        href="https://www.zytales.com/"
        rel="noopener noreferrer"
        target="_blank"
        className="flex items-center w-full h-full !text-base cursor-pointer"
      >
        <img src={icons?.zytalesLogo} alt="zytales" className="w-6 h-6" />
        ZyTales
      </a>
    );
  };

  const modalAccordion = (list, label) => {
    return (
      <CommonAccordion
        accordionClass={{
          boxShadow: "none",
          borderRadius: "8px !important",
          width: "100%",
          "&::before": {
            display: "none",
          },
        }}
        summaryClass={{
          alignItems: "center",
          padding: "8px 24px",
          transition: "none",
          "& .MuiAccordionSummary-content": {
            margin: "0px !important",
          },
          "&.Mui-expanded": {
            minHeight: "0px",
            padding: "12px 24px",
            width: "100%",
          },
        }}
        summaryTypographyClass={{
          fontWeight: "700",
          fontSize: "16px",
        }}
        detailsClass={{
          padding: "0px",
        }}
        title={label}
        children={
          <div className="flex flex-col w-full">
            {list.map((item, index) => (
              <a
                key={index}
                href={item.to}
                rel="noreferrer"
                target="_self"
                className="block !py-3 !px-8 text-grey-700 !text-sm font-medium hover:bg-grey-50"
              >
                {item.label}
              </a>
            ))}
          </div>
        }
        expand={true}
      />
    );
  };

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    // Clean up on unmount
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  useOnClickOutside(menuRef, () => {
    setIsMenuOpen(false);
  });

  DropdownClickOutside(
    examsRef,
    () => {
      setShowExams((prev) => !prev);
    },
    examsLabelRef
  );
  DropdownClickOutside(
    exploreRef,
    () => {
      setShowExplore((prev) => !prev);
    },
    exploreLabelRef
  );
  DropdownClickOutside(
    aboutRef,
    () => {
      setShowAbout((prev) => !prev);
    },
    aboutLabelRef
  );

  return (
    <header
      ref={landingHeaderRef}
      className="sticky top-0 z-50 h-20 px-[clamp(12px,2vw,24px)] w-full border-b-card-width border-grey-border bg-white"
    >
      <div className="flex items-center justify-between w-full h-full">
        <div className="flex">
          <a href="/" className="hidden min-[1400px]:flex h-8">
            <img src={images.zlogo} alt="ZyNerd Logo" className="object-contain h-full" />
          </a>
          <div className="min-[1400px]:hidden flex">
            <div
              className="w-12 p-2 rounded-full hover:bg-grey-50 cursor-pointer"
              onClick={() => {
                setIsMenuOpen(true);
              }}
            >
              <Bars3BottomLeftIcon className="w-8 stroke-2" />
            </div>
          </div>
          <a href="/" className="min-[1400px]:hidden flex w-12 p-2">
            <img src={icons.zynerdlogo} alt="ZyNerd Logo" className="object-contain w-8 h-8" />
          </a>
        </div>
        <div className="flex justify-end gap-3 text-base font-medium">
          <a
            href="https://www.zytales.com/"
            rel="noopener noreferrer"
            target="_blank"
            className="group relative hidden min-[700px]:flex"
          >
            <div className="flex items-center justify-center gap-0.5 rounded-full border border-[#1D4ED8] group-hover:bg-[#EFF6FF] pt-1 pb-0.5 pr-5 pl-4">
              <img src={icons?.zytalesLogo} alt="zytales" className="w-6 h-6" />
              <span className="text-sm font-semibold">
                <span className="text-[#1C4FD8]">Zy</span>
                <span className="text-[#465569]">Tales</span>
              </span>
            </div>
            <span
              className="absolute -top-2 right-0 rounded-full px-2 py-0.5 text-xs font-normal text-white bg-[#1D4ED8] shadow z-50 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                window.open("https://www.zytales.com/", "_blank");
              }}
            >
              New
            </span>
          </a>
          <div
            ref={examsLabelRef}
            onClick={() => {
              setShowExams((prev) => !prev);
            }}
            className="hidden min-[1400px]:flex relative items-center gap-2 !py-2 !pr-3 !pl-4 hover:bg-grey-200 rounded-full cursor-pointer"
          >
            Counsellings
            <ChevronDownIcon
              className={`w-3 stroke-dark transition-transform duration-300 ${
                showExams ? "rotate-180" : "rotate-0"
              }`}
            />
            {showExams && (
              <div
                ref={examsRef}
                className="absolute top-full left-0 w-[272px] mt-2 bg-white shadow-lg rounded-xl border- border-grey-border p-2 cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                {examList.map((item, index) => (
                  <a
                    key={index}
                    href={item.to}
                    rel="noreferrer"
                    target="_self"
                    className="block !px-4 !py-3 text-black text-base font-medium hover:bg-grey-100 rounded-lg"
                    onClick={() => setShowExams(false)}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            )}
          </div>
          <div
            ref={exploreLabelRef}
            onClick={() => {
              setShowExplore((prev) => !prev);
            }}
            className="hidden min-[1200px]:flex relative items-center gap-2 !py-2 !pr-3 !pl-4 hover:bg-grey-200 rounded-full cursor-pointer"
          >
            Explore
            <ChevronDownIcon
              className={`w-3 stroke-dark transition-transform duration-300 ${
                showExplore ? "rotate-180" : "rotate-0"
              }`}
            />
            {showExplore && (
              <div
                ref={exploreRef}
                className="absolute top-full left-0 w-[272px] mt-2 bg-white shadow-lg rounded-xl border-card-width border-grey-border p-2 cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                {exploreList.map((item, index) => (
                  <a
                    key={index}
                    href={item.to}
                    rel="noreferrer"
                    target="_self"
                    className="block !px-4 !py-3 text-black text-base font-medium hover:bg-grey-100 rounded-lg"
                    onClick={() => setShowExplore(false)}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="hidden min-[1084px]:flex !py-2 !px-4 rounded-full hover:bg-grey-200">
            {PackagesOption()}
          </div>

          <div className="hidden min-[965px]:flex !py-2 !px-4 rounded-full hover:bg-grey-200">
            {blogsAndNewsOption()}
          </div>
          <div
            ref={aboutLabelRef}
            onClick={() => {
              setShowAbout((prev) => !prev);
            }}
            className="hidden min-[795px]:flex relative items-center gap-2 !py-2 !pr-3 !pl-4 hover:bg-grey-200 rounded-full cursor-pointer"
          >
            About
            <ChevronDownIcon
              className={`w-3 stroke-dark transition-transform duration-300 ${
                showAbout ? "rotate-180" : "rotate-0"
              }`}
            />
            {showAbout && (
              <div
                ref={aboutRef}
                className="absolute top-full left-0 w-[272px] mt-2 bg-white shadow-lg rounded-xl border-card-width border-grey-border p-2 cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                {aboutUsList.map((item, index) => (
                  <a
                    key={index}
                    href={item.to}
                    rel="noreferrer"
                    target="_self"
                    className="block !px-4 !py-3 text-black text-base font-medium hover:bg-grey-100 rounded-lg"
                    onClick={() => setShowAbout(false)}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2 shrink-0">
            <Link
              to={"/contact-us"}
              aria-label="Contact Us"
              className="group flex gap-2 rounded-4xl cursor-pointer"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-grey-50 group-hover:bg-grey-200">
                <PhoneIcon className="w-4" />
              </div>
              <div className="hidden min-[420px]:flex flex-col font-medium">
                <p className="text-xs leading-5">Talk to our experts</p>
                <p className="text-base">080-690-36000</p>
              </div>
            </Link>
            <a
              href={getDownloadAppLink("/portal/dashboard", { utmLink: getUtmLink() })}
              aria-label="Download ZyNerd app"
              className="flex items-center gap-2 !py-2 !pr-5 !pl-5 min-[568px]:!pl-2 rounded-full bg-black-text-color cursor-pointer"
              onClick={(e) => {
                if (deviceType === "web") {
                  e.preventDefault();
                  const isHome = history.location.pathname === "/";
                  const isPromoPage = history.location.pathname === "/neet-ug-promo";
                  if (!isHome && !isPromoPage) {
                    sessionStorage.setItem("scrollToTarget", true);
                    window.open("/", "_blank");
                  } else {
                    handleScroll();
                  }
                }
              }}
            >
              <div className="hidden min-[568px]:flex items-center justify-center w-6 h-6 rounded-full bg-white">
                <ArrowDownTrayIcon className="w-4 stroke-2" />
              </div>
              <p className="text-sm font-bold text-white">Get the app</p>
            </a>
            <div className="hidden min-[548px]:flex">{loginOption()}</div>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div className="fixed inset-0 min-[1440px]:hidden flex justify-start items-center h-full z-50 bg-black bg-opacity-50">
          <div
            ref={menuRef}
            className="h-full w-[80%] max-w-[456px] rounded-tr-4xl rounded-br-4xl bg-white"
          >
            <div className="flex flex-col justify-between h-full">
              <div className="flex justify-between w-full p-4">
                <a href="/" className="h-8 cursor-pointer">
                  <img src={images.zlogo} alt="ZyNerd Logo" className="object-contain h-full" />
                </a>
                <div
                  className="w-10 h-10 p-2 rounded-full bg-grey-50 cursor-pointer"
                  onClick={() => {
                    setIsMenuOpen(false);
                  }}
                >
                  <XMarkIcon className="w-6" />
                </div>
              </div>
              <div className="h-full overflow-auto">
                <div className="hidden max-[700px]:flex !py-3 !px-6 font-bold hover:bg-grey-50">
                  {ZytalesOption()}
                </div>
                <div className="hidden max-[1400px]:flex">
                  {modalAccordion(examList, " Counsellings")}
                </div>
                <div className="hidden max-[1200px]:flex">
                  {modalAccordion(exploreList, "Explore")}
                </div>
                <div className="hidden max-[1084px]:flex !py-3 !px-6 font-bold hover:bg-grey-50">
                  {PackagesOption()}
                </div>
                <div className="hidden max-[965px]:flex !py-3 !px-6 font-bold hover:bg-grey-50">
                  {blogsAndNewsOption()}
                </div>
                <div className="hidden max-[795px]:flex">
                  {modalAccordion(aboutUsList, " About")}
                </div>
              </div>
              <div className="hidden max-[548px]:flex p-6">{loginOption()}</div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
export default LandingHeaderComponent;
